import { Box, Grid, Typography } from '@mui/material';
import { type ReactElement, useState } from 'react';

import type { QuotePolicy } from '@core/types/api/QuotePolicy/QuotePolicy';
import { SlabButton } from '@web/components/SlabButton/SlabButton';
import { SlabDrawerLayout } from '@web/components/SlabDrawer/SlabDrawerLayout';

type QuoteDrawerViolationsPageProps = {
  violatedPolicies: QuotePolicy[];
  submitForm: () => Promise<void> | void;
  submitForReview: () => Promise<void> | void;
  isSubmitPending: boolean;
};

export const QuoteDrawerViolationsPage = ({
  violatedPolicies,
  submitForm,
  submitForReview,
  isSubmitPending,
}: QuoteDrawerViolationsPageProps): ReactElement => {
  const [actionTaken, setActionTaken] = useState<'saveDraft' | 'submitForReview'>();
  return (
    <SlabDrawerLayout>
      <Box>
        <Typography variant='h1' fontSize='2rem'>
          Issues Found
        </Typography>

        {violatedPolicies.map((policy) => (
          <Box key={policy.id} margin='2rem 0'>
            <Typography variant='h3' fontSize='1.5rem'>
              {policy.name}
            </Typography>
            <p>
              {policy.reviewerNamesDisplay()} must approve if the quote is not edited to meet these
              criteria:
            </p>
            <ul>
              {policy.thresholds.map((threshold) => (
                <li key={`${threshold.kind}|${threshold.thresholdValue}`}>
                  {threshold.description}
                </li>
              ))}
              {policy.tierDeviationCriteria().map((criterion) => (
                <li key={criterion}>{criterion}</li>
              ))}
            </ul>
          </Box>
        ))}
      </Box>

      <Grid container spacing={2} sx={{ '& button': { width: '100%' } }}>
        <Grid item xs={4}>
          <SlabButton
            variant='slab-secondary'
            onClick={() => {
              setActionTaken('saveDraft');
              submitForm();
            }}
            disabled={isSubmitPending}
            loading={isSubmitPending && actionTaken === 'saveDraft'}
          >
            Save as Draft
          </SlabButton>
        </Grid>
        <Grid item xs={4}>
          <SlabButton
            variant='slab-secondary'
            onClick={() => {
              setActionTaken('submitForReview');
              submitForReview();
            }}
            disabled={isSubmitPending}
            loading={isSubmitPending && actionTaken === 'submitForReview'}
          >
            Submit for Review
          </SlabButton>
        </Grid>
        <Grid item xs={4}>
          <SlabButton variant='slab-primary' disabled>
            Send To Customer
          </SlabButton>
        </Grid>
      </Grid>
    </SlabDrawerLayout>
  );
};
